import {SegmentedControl} from '@primer/react'
import {use} from 'react'
import {type CommentsFilterState, type AuthorFilterItem, CommentsFilter} from './CommentsFilter'
import {CommentsContext, ViewMode} from './CommentsContext'

export function isReviewCommentsViewMode(viewMode: ViewMode): boolean {
  return viewMode === ViewMode.ReviewComments || viewMode === ViewMode.ThreadPreviews
}

interface CommentsViewControlsProps {
  threadPreviewsAuthorList: AuthorFilterItem[]
  issueCommentsAuthorList: AuthorFilterItem[]
  threadPreviewFilterState: CommentsFilterState
  setThreadPreviewFilterState: (state: CommentsFilterState) => void
  issueCommentsFilterState: CommentsFilterState
  setIssueCommentsFilterState: (state: CommentsFilterState) => void
  iconOnly?: boolean
}

/**
 * Renders view controls (filter, sort, etc) for comments.
 */
export function CommentsViewControls({
  threadPreviewsAuthorList,
  issueCommentsAuthorList,
  threadPreviewFilterState,
  setThreadPreviewFilterState,
  issueCommentsFilterState,
  setIssueCommentsFilterState,
  iconOnly,
}: CommentsViewControlsProps) {
  const {viewMode, setViewMode} = use(CommentsContext)
  const isReviewCommentsMode = isReviewCommentsViewMode(viewMode)

  // Review and conversation each keep their own filter state.
  return (
    <div className="d-flex flex-row flex-items-center gap-2 py-2">
      <SegmentedControl
        aria-label="Comment view mode"
        onChange={(value: number) => {
          const nextViewMode = value === 0 ? ViewMode.ThreadPreviews : ViewMode.IssueComments
          setViewMode(nextViewMode)
        }}
      >
        <SegmentedControl.Button selected={isReviewCommentsMode}>Review</SegmentedControl.Button>
        <SegmentedControl.Button selected={viewMode === ViewMode.IssueComments}>Conversation</SegmentedControl.Button>
      </SegmentedControl>
      {isReviewCommentsMode && (
        <CommentsFilter
          authorList={threadPreviewsAuthorList}
          className="width-full"
          filterState={threadPreviewFilterState}
          onFilterStateChange={setThreadPreviewFilterState}
          hideReviewThreadFilters={false}
          iconOnly={iconOnly}
        />
      )}
      {viewMode === ViewMode.IssueComments && (
        <CommentsFilter
          authorList={issueCommentsAuthorList}
          className="width-full"
          filterState={issueCommentsFilterState}
          onFilterStateChange={setIssueCommentsFilterState}
          hideReviewThreadFilters
          iconOnly={iconOnly}
        />
      )}
    </div>
  )
}
